import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable,of} from 'rxjs';
import { map, share, take } from 'rxjs/operators';
import { User } from '../models/user';
import { UserManagementApiService } from './UserManagementApiService';
import { BasicResponse } from '../responses/basicResponse';
import { Customer } from '../models/customer';
import { UserRoleResponse } from '../responses/userRoleResponse';
import { UserRole } from '../models/userRole';
import { UserResponse } from '../responses/userResponse';
import { AuthenticationService } from './AuthenticationService';
import { CustomerResponse } from '../responses/customerResponse';
import { Client } from '../models/client';
import { ClientResponse } from '../responses/clientResponse';
import { SFTPUser } from '../models/sftpUser';
import { SFTPUserResponse } from '../responses/sftpUserResponse';
import { SFTPHostResponse } from '../responses/sftpHostResponse';
import { CustomerBranding } from '../models/customerBranding';
import { CustomerBrandingResponse } from '../responses/customerBrandingResponse';
import { PremierPartner } from '../models/premierPartner';
import { PremierServiceType } from '../models/premierServiceType';
import { PremierPartnerResponse } from '../responses/premierPartnerResponse';
import { PremierServiceTypeReponse } from '../responses/premierServiceTypeResponse';
import { PremierServiceProductType } from '../models/premierServiceProductType';
import { PremierServiceProductTypeResponse } from '../responses/premierServiceProductTypeResponse';
import { PremierServiceProduct } from '../models/premierServiceProduct';
import { CustomerPremierPartnerServiceConfiguration } from '../models/customerPremierPartnerServiceConfiguration';
import { CustomerPremierPartnerServiceConfigurationResponse } from '../responses/customerPremierPartnerServiceConfigurationResponse';
import { UserDTO } from '../DTO/userDTO';
import { CustomerDTO } from '../DTO/customerDTO';
import { ClientDTO } from '../DTO/clientDTO';
import { SFTPUserDTO } from '../DTO/sftpUserDTO';
import { CustomerBrandingDTO } from '../DTO/customerBrandingDTO';
import { QBIsEntityAuthorizedResponse } from '../responses/qbIsEntityAuthorizedResponse';
import { AuthorizedSenderEmailAddress } from '../models/authorizedSenderEmailAddress';
import { AuthorizedSenderEmailAddressResponse } from '../responses/authorizedSenderEmailAddressResponse';
import { AuthorizedSenderEmailAddressDTO } from '../DTO/authorizedSenderEmailAddressDTO';
import { QBAllAccountsResponse } from '../responses/qbAllAccountsResponse';
import { CustomerSecurityCompliance } from '../models/customerSecurityCompliance';
import { CustomerSecurityComplianceResponse } from '../responses/customerSecurityComplianceResponse';
import { CustomerSecurityComplianceDTO } from '../DTO/customerSecurityComplianceDTO';
import { QBAllClassesResponse } from '../responses/qbAllClassesResponse';
import { QBAllDepartmentsResponse } from '../responses/qbAllDepartmentsResponse';
import { CustomerFeeSchedule } from '../models/customerFeeSchedule';
import { CustomerFeeScheduleResponse } from '../responses/customerFeeScheduleResponse';
import { XeroIsEntityAuthorizedResponse } from '../responses/xeroIsEntityAuthorizedResponse';
import { XeroAllAccountsResponse } from '../responses/xeroAllAccountsResponse';
import { XeroAllTrackingCategoriesResponse } from '../responses/xeroAllTrackingCategoriesResponse';
import { DropboxIsEntityAuthorizedResponse } from '../responses/dropboxIsEntityAuthorizedResponse';
import { MicrosoftIsEntityAuthorizedResponse } from '../responses/microsoftIsEntityAuthorizedResponse';
import { FileSharingAccount } from '../models/fileSharingAccount';
import { CustomerFileSharingAccountResponse } from '../responses/customerFileSharingAccountResponse';
import { FileSharingAccountDTO } from '../DTO/fileSharingAccountDTO';
import { Vendor } from '../models/vendor';
import { VendorResponse } from '../responses/vendorResponse';
import { VendorDTO } from '../DTO/vendorDTO';
import { BillingBucket } from '../models/billingBucket';
import { BillingBucketResponse } from '../responses/billingBucketResponse';
import { BillingBucketDTO } from '../DTO/billingBucketDTO';
import { PremierServiceProductsReponse } from '../responses/premierServiceProductsResponse';
import { UsersReportResponse } from '../responses/usersReportResponse';
import { UserReport } from '../models/userReport';
import { PrismHRAPIPermissionsResponse } from '../responses/prismHRAPIPermissionsResponse';
import { WizardSession } from '../models/wizardSession';
import { WizardSessionResponse } from '../responses/wizardSessionResponse';

@Injectable({ providedIn: 'root' })
export class AccountService {
    private sftpHostObservableCache?: Observable<{host:string,port:number}>;
    private sftpHostCache?:{host:string,port:number};
   
    private userRoleObservableCache?: Observable<UserRole[]>;
    private userRoleCache?: UserRole[];

    private userObservableCache: {[entityRef:string]: Observable<User[]> | undefined} = {}; //entitRef is composite of entityType and entityId. ex: C123 or V345
    private userCache: {[entityRef:string]: User[]|undefined} = {};

    private userNameObservableCache: {[userId:number]: Observable<string> | undefined} = {};
    private userNameCache: {[userId:number]: string | undefined} = {};

    private customerObservableCache?: Observable<Customer[]>;
    private customerCache?: Customer[];

    private vendorObservableCache?: Observable<Vendor[]>;
    private vendorCache?: Vendor[];

    private clientObservableCache: {[customerId:number]: Observable<Client[]>|undefined} = {};
    private clientCache: {[customerId:number]: Client[] | undefined} = {};

    private sftpUserObservableCache: {[customerId:number]: Observable<SFTPUser[]> | undefined} = {};
    private sftpUserCache: {[customerId:number]: SFTPUser[] | undefined} = {};

    private fileSharingAccountObservableCache: {[customerId:number]: Observable<FileSharingAccount[]> | undefined} = {};
    private fileSharingAccountCache: {[customerId:number]: FileSharingAccount[] | undefined} ={};

    private authorizedSenderEmailAddressObservableCache: {[customerId:number]: Observable<AuthorizedSenderEmailAddress[]> | undefined} = {};
    private authorizedSenderEmailAddressCache: {[customerId:number]: AuthorizedSenderEmailAddress[] | undefined} = {};

    private customerBrandingObservableCache: { [customerId: number]: Observable<CustomerBranding[]> | undefined} = {};
    private customerBrandingCache: { [customerId: number]: CustomerBranding[] |undefined } = {};

    private customerSecurityComplianceObservableCache: { [customerId: number]: Observable<CustomerSecurityCompliance | undefined>} = {};
    private customerSecurityComplianceCache: { [customerId: number]: CustomerSecurityCompliance | undefined} = {};

    private premierServiceTypeObservableCache?: Observable<PremierServiceType[]>;
    private premierServiceTypeCache?: PremierServiceType[];

    private premierServiceProductTypeObservableCache?: Observable<PremierServiceProductType[]>;
    private premierServiceProductTypeCache?: PremierServiceProductType[];

    private premierServiceProductObservableCache?: Observable<PremierServiceProduct[]>;
    private premierServiceProductCache?: PremierServiceProduct[];


    private premierPartnersObservableCache?: Observable<PremierPartner[]>;
    private premierPartnerCache?: PremierPartner[];

    private customerPremierPartnerServiceConfigurationObservableCache?: Observable<CustomerPremierPartnerServiceConfiguration[]>;
    private customerPremierPartnerServiceConfigurationCache?: CustomerPremierPartnerServiceConfiguration[];

    private billingBucketObservableCache?: Observable<BillingBucket[]>;
    private billingBucketCache?: BillingBucket[];

    constructor(private authService:AuthenticationService, private umApiService:UserManagementApiService, private router:Router) {
        this.authService.currentUserSubject.subscribe(au=>this.processCurrentUser(au));
        this.authService.currentCustomerContextSubject.subscribe(customer => this.processCurrentCustomerContext(customer));
        this.authService.userMFAChangedSubject.subscribe(data => this.userChanged(data));
    }

    private processCurrentUser(u:User | undefined){
        if(!u){
            //user logged out, clear all caches
            this.clearCache();
        }
    }

    private processCurrentCustomerContext(customer:Customer | undefined){
        this.clearCustomerContextCache();
    }

    private userChanged(data:{id:number,twoFactorEnabled:boolean} | undefined){
        if(this.userCache){
            let usr=undefined;
            if(data){
                if(this.authService.customerContext) usr = this.userCache[this.authService.customerContext.id]?.find(u=>u.id==data.id);
                else usr = this.userCache[0]?.find(u=>u.id==data.id);
                if(usr) usr.twoFactorEnabled=data.twoFactorEnabled;
            }
        }
    }

    private clearCustomerContextCache(){
        this.premierPartnerCache=undefined;
        this.customerPremierPartnerServiceConfigurationCache=undefined;
    }

    private clearCache(){
        this.userCache={};
        this.userNameCache={};
        this.userRoleCache=undefined;
        this.customerCache=undefined;
        this.clientCache={};
        this.premierServiceTypeCache=undefined;
        this.clearCustomerContextCache();
    }

    get authorized(){
        return this.authService.authorizedSubject.value;
    }

    //#region user roles
    public getUserRoles(){
        if(!this.authorized) return of([]);
        // Data available
        if (this.userRoleCache) return of(this.userRoleCache);
        // Request pending
        else if (this.userRoleObservableCache) return this.userRoleObservableCache;
        // New request needed
        else this.userRoleObservableCache = this.fetchUserRoles(false);

        return this.userRoleObservableCache;
    }

    public fetchUserRoles(deleted:boolean){
        let requestorId = this.authService.currentUserValue!.id;
        return this.umApiService.getUserRoles(requestorId).pipe(map((r)=>this.processUserRolesResult(r)), share());
    }

    private processUserRolesResult(r:UserRoleResponse):UserRole[]{
        this.userRoleObservableCache=undefined;
        var userRoles = new Array<UserRole>();
        if(r.success && r.userRoleList) {
            userRoles = r.userRoleList.map(dto=>UserRole.mapFromDTO(dto));
        }
        this.userRoleCache=userRoles;
        return userRoles;
    }

    public getUserRoleById(roleId:number){
        if(!this.userRoleCache){
            //nothing loaded yet or update forced, load all users and filter
            return this.getUserRoles().pipe(map(r=>r!.find(r=>r.id==roleId)));
        }
        else{
            //we have a cache, lets check if have the requested user
            return of (this.userRoleCache.find(r=>r.id==roleId));
        }
    }
    //#endregion

    //#region user
    public getUsers(entityId:number, entityType:string, forceReload:boolean){
        let entityRef=entityType+entityId;
        if(forceReload) this.userCache[entityRef]=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.userCache[entityRef]) return of(this.userCache[entityRef]);
        // Request pending
        else if (this.userObservableCache[entityRef]) return this.userObservableCache[entityRef];
        // New request needed
        else this.userObservableCache[entityRef] = this.fetchUsers(entityId, entityType);

        return this.userObservableCache[entityRef];
    }

    private fetchUsers(entityId:number, entityType:string){
        return this.umApiService.getUsers(entityId, entityType).pipe(map((r)=>this.processUsersResult(r, entityId, entityType)), share());
    }

    private processUsersResult(r:UserResponse, entityId:number, entityType:string):User[]{
        let entityRef=entityType+entityId;
        this.userObservableCache[entityRef]=undefined;
        var users = new Array<User>();
        if(r.success && r.userList) {
            users = r.userList.map(dto=>this.processUserDTO(dto));
        }
        this.userCache[entityRef]=users;
        return users;
    }

    private processUserDTO(dto:UserDTO):User{
        return User.mapFromDTO(dto)
    }

    //for reporting only, no caching
    public getAllUsers(activeOnly:boolean){
        return this.umApiService.getAllUsers(activeOnly).pipe(map((r)=>this.processAllUsersResult(r)), share());
    }

    private processAllUsersResult(r:UsersReportResponse):UserReport[]{
        if(r.success) {
            var userReportList:UserReport[] = [];
            r.userReportList.forEach(dto=>{ userReportList.push(UserReport.mapFromDTO(dto)); } );
            return userReportList;
        }
        return [];
    }

    public getUserById(entityId:number, entityType:string, userId:number){
        let entityRef=entityType+entityId;
        if(!this.userCache[entityRef]){
            //nothing loaded yet or update forced, load all users and filter
            return this.getUsers(entityId, entityType, false).pipe(map(u=>u!.find(u=>u.id==userId)));
        }
        else{
            //we have a cache, lets check if have the requested user
            return of (this.userCache[entityRef].find(u=>u.id==userId));
        }
    }

    public getUserName(userId:number){
        if(!this.authorized) return of('');
        // Data available
        if (this.userNameCache[userId]) return of(this.userNameCache[userId]);
        // Request pending
        else if (this.userNameObservableCache[userId]) return this.userNameObservableCache[userId];
        // New request needed
        else this.userNameObservableCache[userId] = this.fetchUserName(userId);

        return this.userNameObservableCache[userId];
    }

    public fetchUserName(userId:number){
        return this.umApiService.getUserName(userId).pipe(map((r)=>this.processUserNameResult(r, userId)), share());
    }

    private processUserNameResult(r:BasicResponse, userId:number):string{
        this.userNameObservableCache[userId]=undefined;
        if(r.success) {
            this.userNameCache[userId]=r.message;
            return r.message;
        }
        else{
            return "unknown";
        }
    }

    public createUser(u:User, entityId:number, entityType:string):Observable<any>{
        return this.umApiService.createUser(u, entityId, entityType).pipe(map((r)=>this.processCreateUserResult(r)));
    }

    private processCreateUserResult(r:UserResponse){
        if(r.success && r.userList && r.userList.length>0) {
            var u = this.processUserDTO(r.userList[0]);
            var entityRef = (u.role.id==20 || u.role.id==30)?('C'+u.customerId):((u.role.id==50 || u.role.id==60)?('V'+u.vendorId):undefined);
            if(entityRef){
                if(!this.userCache[entityRef])this.userCache[entityRef]=[];
                this.userCache[entityRef]!.push(u);
            }
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public updateUser(u:User):Observable<any>{
        return this.umApiService.updateUser(u).pipe(map((r)=>this.processUpdateUserResult(r)));
    }

    private processUpdateUserResult(r:UserResponse){
        if(r.success && r.userList && r.userList.length>0) {
            var u = this.processUserDTO(r.userList[0]);
            var entityRef = (u.role.id==20 || u.role.id==30)?('C'+u.customerId):((u.role.id==50 || u.role.id==60)?('V'+u.vendorId):undefined);
            if(entityRef && this.userCache[entityRef]){
                let index = this.userCache[entityRef]!.findIndex(usr=>usr.id==u.id);
                if(index>-1){
                    this.userCache[entityRef]![index]=u;
                }
                else{
                    this.userCache[entityRef]!.push(u);
                }
            }
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }


    public lockUser(user:User){
        return this.umApiService.lockUser(user.id).pipe(map((r)=>this.processLockUserResult(user,r)));
    }

    private processLockUserResult(user:User, r:BasicResponse){
        if(r.success){
            user.lockoutEnabled=true;
        }
        return r;
    }

    public unlockUser(user:User){
        return this.umApiService.unlockUser(user.id).pipe(map((r)=>this.processUnlockUserResult(user,r)));
    }

    private processUnlockUserResult(user:User, r:BasicResponse){
        if(r.success){
            user.lockoutEnabled=false;
        }
        return r;
    }

    public deleteUser(user:User, physicalDelete:boolean){
        return this.umApiService.deleteUser(user.id,physicalDelete).pipe(map((r)=>this.processDeleteUserResult(user,r)));
    }

    private processDeleteUserResult(user:User, r:BasicResponse){
        if(r.success){
            //mark as deleted
            user.deleted=true;
        }
        return r;
    }

    public undeleteUser(user:User){
        return this.umApiService.undeleteUser(user.id).pipe(map((r)=>this.processUndeleteUserResult(user,r)));
    }

    private processUndeleteUserResult(user:User, r:BasicResponse){
        if(r.success){
            //mark as undeleted
            user.deleted=false;
        }
        return r;
    }

    public adminPwdChange(user:User, newPassword:string){
        return this.umApiService.adminPwdChange(user.id, newPassword).pipe(map((r)=>this.processAdminPwdChangeResult(user,r)));
    }
    private processAdminPwdChangeResult(user:User, r:BasicResponse){
        if(r.success){
           //nothing to do here
        }
        return r;
    }
    //#endregion

    //#region customer
    public getCustomers(forceReload:boolean){
        if(forceReload) this.customerCache=undefined;
        if(!this.authorized) return of([])
        // Data available
        if (this.customerCache) return of(this.customerCache);
        // Request pending
        else if (this.customerObservableCache) return this.customerObservableCache;
        // New request needed
        else this.customerObservableCache = this.fetchCustomers();

        return this.customerObservableCache;
    }

    public fetchCustomers(){
        return this.umApiService.getCustomers().pipe(map((r)=>this.processCustomersResult(r)), share());
    }

    private processCustomersResult(r:CustomerResponse):Customer[]{
        this.customerObservableCache=undefined;
        var customers = new Array<Customer>();
        if(r.success && r.customerList) {
            customers = r.customerList.map(dto=>this.processCustomerDTO(dto));
        }
        this.customerCache=customers;
        return customers;
    }

    private processCustomerDTO(dto:CustomerDTO):Customer{
        return Customer.mapFromDTO(dto);
    }

    public getCustomerById(customerId:number){
        if(!this.customerCache){
            //nothing loaded yet or update forced, load all and filter
            return this.getCustomers(false).pipe(map(c=>c!.find(c=>c.id==customerId)));
        }
        else{
            //we have a cache, lets check if have the requested
            return of (this.customerCache.find(c=>c.id==customerId));
        }
    }

    public createCustomer(c:Customer, u:User):Observable<any>{
        return this.umApiService.createCustomer(c, u).pipe(map((r)=>this.processCreateCustomerResult(r)));
    }

    private processCreateCustomerResult(r:CustomerResponse){
        if(r.success && r.customerList && r.customerList.length>0) {
            var c = this.processCustomerDTO(r.customerList[0]);
            this.customerCache!.push(c);
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public updateCustomer(c:Customer){
        return this.authService.updateCustomer(c).pipe(map((r)=>this.processUpdateCustomerResult(r)));
    }

    private processUpdateCustomerResult(r:CustomerResponse){
        if(r.success && r.customerList && r.customerList.length>0) {
            var c = this.processCustomerDTO(r.customerList[0]);
            if(this.customerCache){
                let index = this.customerCache.findIndex(cus=>cus.id==c.id);
                if(index>-1){
                    this.customerCache[index]=c;
                }
                else{
                    this.customerCache.push(c);
                }
            }
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public deleteCustomer(customer:Customer, physicalDelete:boolean){
        return this.umApiService.deleteCustomer(customer.id,physicalDelete).pipe(map((r)=>this.processDeleteCustomerResult(customer,r)));
    }

    private processDeleteCustomerResult(customer:Customer, r:BasicResponse){
        if(r.success){
            customer.deleted=true;
        }
        return r;
    }

    public undeleteCustomer(customer:Customer){
        return this.umApiService.undeleteCustomer(customer.id).pipe(map((r)=>this.processUndeleteCustomerResult(customer,r)));
    }

    private processUndeleteCustomerResult(customer:Customer, r:BasicResponse){
        if(r.success){
            customer.deleted=false;
        }
        return r;
    }

    public getCustomerAbstractsForClientIds(clientIdList:number[]){
        let entityId = this.authService.entityContextId;
        let entityType = this.authService.entityContextType;
        return this.umApiService.getCustomerAbstractsForClientIds(entityId,entityType,clientIdList);
    }
    //#endregion

    //#region vendor
    public getVendors(forceReload:boolean){
        if(forceReload) this.vendorCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.vendorCache) return of(this.vendorCache);
        // Request pending
        else if (this.vendorObservableCache) return this.vendorObservableCache;
        // New request needed
        else this.vendorObservableCache = this.fetchVendors();

        return this.vendorObservableCache;
    }

    public fetchVendors(){
        return this.umApiService.getVendors().pipe(map((r)=>this.processVendorsResult(r)), share());
    }

    private processVendorsResult(r:VendorResponse):Vendor[]{
        this.vendorObservableCache=undefined;
        var vendors = new Array<Vendor>();
        if(r.success && r.vendorList) {
            vendors = r.vendorList.map(dto=>this.processVendorDTO(dto));
        }
        this.vendorCache=vendors;
        return vendors;
    }

    private processVendorDTO(dto:VendorDTO):Vendor{
        return Vendor.mapFromDTO(dto);
    }

    public getVendorById(vendorId:number){
        if(!this.vendorCache){
            //nothing loaded yet or update forced, load all and filter
            return this.getVendors(false).pipe(map(v=>v!.find(c=>c.id==vendorId)));
        }
        else{
            //we have a cache, lets check if have the requested
            return of (this.vendorCache.find(v=>v.id==vendorId));
        }
    }

    public createVendor(v:Vendor, u:User):Observable<any>{
        return this.umApiService.createVendor(v, u).pipe(map((r)=>this.processCreateVendorResult(r)));
    }

    private processCreateVendorResult(r:VendorResponse){
        if(r.success && r.vendorList && r.vendorList.length>0) {
            let v = this.processVendorDTO(r.vendorList[0]);
            this.vendorCache!.push(v);
            this.processBillingBucketRelation(v);
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public updateVendor(v:Vendor){
        return this.umApiService.updateVendor(v).pipe(map((r)=>this.processUpdateVendorResult(r)));
    }

    private processUpdateVendorResult(r:VendorResponse){
        if(r.success && r.vendorList && r.vendorList.length>0) {
            var v = this.processVendorDTO(r.vendorList[0]);
            let index = this.vendorCache!.findIndex(vendor=>vendor.id==v.id);
            if(index>-1){
                this.vendorCache![index]=v;
            }
            else{
                this.vendorCache!.push(v);
            }
            this.processBillingBucketRelation(v);
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    private processBillingBucketRelation(v:Vendor){
        //relate with billing bucket object if any
        if(v.billingBucketIdList && this.billingBucketCache){
            v.billingBucketIdList.forEach(id=>{
                let billingBucket = this.billingBucketCache!.find(bb=>bb.id==id);
                billingBucket!.vendor=v;
            });
        }
        //unrelate with billing bucket if not referenced at updated vendor anymore
        if(this.billingBucketCache){
            this.billingBucketCache.forEach(bb=>{
                if(bb.vendorId==v.id && v.billingBucketIdList.findIndex(bbId=>bbId==bb.id)==-1) bb.vendor=undefined;
            });
        }
    }

    public deleteVendor(v:Vendor, physicalDelete:boolean){
        return this.umApiService.deleteVendor(v.id,physicalDelete).pipe(map((r)=>this.processDeleteVendorResult(v,r)));
    }

    private processDeleteVendorResult(v:Vendor, r:BasicResponse){
        if(r.success){
            v.deleted=true;
        }
        return r;
    }

    public undeleteVendor(v:Vendor){
        return this.umApiService.undeleteVendor(v.id).pipe(map((r)=>this.processUndeleteVendorResult(v,r)));
    }

    private processUndeleteVendorResult(v:Vendor, r:BasicResponse){
        if(r.success){
            v.deleted=false;
        }
        return r;
    }

    //#endregion

    //#region client
    public getClients(customerId:number, forceReload:boolean){
        if(forceReload) this.clientCache[customerId]=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.clientCache[customerId]) return of(this.clientCache[customerId]);
        // Request pending
        else if (this.clientObservableCache[customerId]) return this.clientObservableCache[customerId];
        // New request needed
        else this.clientObservableCache[customerId] = this.fetchClients(customerId);

        return this.clientObservableCache[customerId];
    }

    public fetchClients(customerId:number){
        return this.umApiService.getCustomerClients(customerId).pipe(map((r)=>this.processClientsResult(r, customerId)), share());
    }

    private processClientsResult(r:ClientResponse, customerId:number):Client[]{
        this.clientObservableCache[customerId]=undefined;
        var clients = new Array<Client>();
        if(r.success && r.clientList) {
            clients = r.clientList.map(dto=>this.processClientDTO(dto));
        }
        this.clientCache[customerId]=clients;
        return clients;
    }

    private processClientDTO(dto:ClientDTO):Client{
        return Client.mapFromDTO(dto);
    }

    public getClientById(customerId:number, clientId:number){
        if(!this.clientCache[customerId]){
            //nothing loaded yet or update forced, load all clients and filter
            return this.getClients(customerId, false).pipe(map(c=>c!.find(c=>c.id==clientId)));
        }
        else{
            //we have a cache, lets check if have the requested client
            return of (this.clientCache[customerId].find(c=>c.id==clientId));
        }
    }

    public getClientByName(customerId:number, clientName:string){
        if(!this.clientCache[customerId]){
            //nothing loaded yet or update forced, load all clients and filter
            return this.getClients(customerId, false).pipe(map(c=>c!.find(c=>c.name==clientName)));
        }
        else{
            //we have a cache, lets check if have the requested client
            return of (this.clientCache[customerId].find(c=>c.name==clientName));
        }
    }

    public createClient(cl:Client):Observable<any>{
        return this.umApiService.createClient(cl).pipe(map((r)=>this.processCreateClientResult(r)));
    }

    private processCreateClientResult(r:ClientResponse){
        if(r.success && r.clientList && r.clientList.length>0) {
            var c = this.processClientDTO(r.clientList[0]);
            if(!this.clientCache[c.customerId])this.clientCache[c.customerId]=[];
            this.clientCache[c.customerId]!.push(c);
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public updateClient(c:Client){
        //TODO: not yet implemented in UI
        return this.umApiService.updateClient(c).pipe(map((r)=>this.processUpdateClientResult(r)));
    }

    private processUpdateClientResult(r:ClientResponse){
        if(r.success && r.clientList && r.clientList.length>0) {
            var c = this.processClientDTO(r.clientList[0]);
            let index = this.clientCache[c.customerId]!.findIndex(cl=>cl.id==c.id);
            if(index>-1){
                this.clientCache[c.customerId]![index]=c;
            }
            else{
                this.clientCache[c.customerId]!.push(c);
            }
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }
    //#endregion

    //#region sftp users
    public getSFTPUsers(customerId:number, forceReload:boolean){
        if(forceReload) this.sftpUserCache[customerId]=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.sftpUserCache[customerId]) return of(this.sftpUserCache[customerId]);
        // Request pending
        else if (this.sftpUserObservableCache[customerId]) return this.sftpUserObservableCache[customerId];
        // New request needed
        else this.sftpUserObservableCache[customerId] = this.fetchSFTPUsers(customerId);

        return this.sftpUserObservableCache[customerId];
    }

    public fetchSFTPUsers(customerId:number){
        return this.umApiService.getSFTPUsers( customerId).pipe(map((r)=>this.processSFTPUsersResult(r, customerId)), share());
    }

    private processSFTPUsersResult(r:SFTPUserResponse, customerId:number):SFTPUser[]{
        this.clientObservableCache[customerId]=undefined;
        var sftpUsers = new Array<SFTPUser>();
        if(r.success && r.sftpUserList) {
            sftpUsers = r.sftpUserList.map(dto=>this.processSFTPUserDTO(dto));
        }
        this.sftpUserCache[customerId]=sftpUsers;
        return sftpUsers;
    }

    private processSFTPUserDTO(dto:SFTPUserDTO):SFTPUser{
        var sftpUser = SFTPUser.mapFromDTO(dto);
        if(this.authService.isSuperAdmin){
            this.getCustomerById(sftpUser.customerId).pipe(take(1)).subscribe(c=> sftpUser.customer = c!);
        }
        else{
            sftpUser.customer = this.authService.customerContext!;
        }
        return sftpUser;
    }

    public createSFTPUser(u:SFTPUser):Observable<any>{
        return this.umApiService.createSFTPUser(u).pipe(map((r)=>this.processCreateSFTPUserResult(r)));
    }

    private processCreateSFTPUserResult(r:SFTPUserResponse){
        if(r.success && r.sftpUserList && r.sftpUserList.length>0) {
            var s = this.processSFTPUserDTO(r.sftpUserList[0]);
            if(!this.sftpUserCache[s.customerId])this.sftpUserCache[s.customerId]=[];
            this.sftpUserCache[s.customerId]!.push(s);
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public updateSFTPUser(u:SFTPUser){
        return this.umApiService.updateSFTPUser(u).pipe(map((r)=>this.processUpdateSFTPUserResult(r)));
    }

    private processUpdateSFTPUserResult(r:SFTPUserResponse){
        if(r.success && r.sftpUserList && r.sftpUserList.length>0) {
            var s = this.processSFTPUserDTO(r.sftpUserList[0]);
            let index = this.sftpUserCache[s.customerId]!.findIndex(su=>su.id==s.id);
            if(index>-1){
                this.sftpUserCache[s.customerId]![index]=s;
            }
            else{
                this.sftpUserCache[s.customerId]!.push(s);
            }
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public enableSFTPUser(u:SFTPUser, enable:boolean){
        return this.umApiService.enableSFTPUser(u.id, enable).pipe(map((r)=>this.processEnableSFTPUserResult(u, enable, r)));
    }

    private processEnableSFTPUserResult(u:SFTPUser, enable:boolean, r:BasicResponse){
        if(r.success) {
           u.enabled=enable;
        }
        return r;
    }

    public changeSFTPUserPassword(u:SFTPUser, newPassword:string){
        return this.umApiService.changeSFTPUserPassword(u.id, newPassword).pipe(map((r)=>this.processChangeSFTPUserPasswordResult(u, newPassword, r)));
    }

    private processChangeSFTPUserPasswordResult(u:SFTPUser, newPassword:string, r:BasicResponse){
        if(r.success) {
           u.sftpPassword=newPassword;
        }
        return r;
    }

    public getSFTPHost(){
        // Data available
        if (this.sftpHostCache) return of (this.sftpHostCache);
        // Request pending
        else if (this.sftpHostObservableCache) return this.sftpHostObservableCache;
        // New request needed
        else {
            this.sftpHostObservableCache = this.umApiService.getSFTPHost().pipe(map((r:SFTPHostResponse)=>{
                this.sftpHostObservableCache=undefined;
                this.sftpHostCache={host:r.host,port:r.port};
                return this.sftpHostCache;
            }), share());
            return this.sftpHostObservableCache;
        }
    }
    //#endregion

    //#region authorized sender email address
    public getAuthorizedSenderEmailAddresses(customerId:number, forceReload:boolean){
        if(forceReload) this.authorizedSenderEmailAddressCache[customerId]=undefined;
        if(!this.authorized) return of([]);
        //Data available
        if(this.authorizedSenderEmailAddressCache[customerId]) return of(this.authorizedSenderEmailAddressCache[customerId]);
        //Request pending
        else if(this.authorizedSenderEmailAddressObservableCache[customerId]) return this.authorizedSenderEmailAddressObservableCache[customerId];
        //New request needed
        else this.authorizedSenderEmailAddressObservableCache[customerId] = this.fetchAuthorizedSenderEmailAddresses(customerId);

        return this.authorizedSenderEmailAddressObservableCache[customerId];
    }

    public fetchAuthorizedSenderEmailAddresses(customerId:number){
        return this.umApiService.getAuthorizedSenderEmailAddresses(customerId).pipe(map((r)=>this.processAuthorizedSenderEmailAddressesResult(r, customerId)), share());

    }

    private processAuthorizedSenderEmailAddressesResult(r:AuthorizedSenderEmailAddressResponse, customerId:number):AuthorizedSenderEmailAddress[]{
        this.authorizedSenderEmailAddressObservableCache[customerId]=undefined;
        var authorizedSenderEmailAddresses = new Array<AuthorizedSenderEmailAddress>();
        if(r.success && r.authorizedSenderEmailAddressList){
            authorizedSenderEmailAddresses = r.authorizedSenderEmailAddressList.map(dto=>this.processAuthorizedSenderEmailAddressDTO(dto));
        }
        this.authorizedSenderEmailAddressCache[customerId]=authorizedSenderEmailAddresses;
        return authorizedSenderEmailAddresses;
    }

    private processAuthorizedSenderEmailAddressDTO(dto:AuthorizedSenderEmailAddressDTO):AuthorizedSenderEmailAddress{
        var authorizedSenderEmailAddress = AuthorizedSenderEmailAddress.mapFromDTO(dto);
        if(this.authService.isSuperAdmin){
            this.getCustomerById(authorizedSenderEmailAddress.customerId).pipe(take(1)).subscribe(c=>authorizedSenderEmailAddress.customerId = c!.id);
        }
        else{
            authorizedSenderEmailAddress.customerId = this.authService.customerContext!.id;
        }
        return authorizedSenderEmailAddress;
    }

    public createAuthorizedSenderEmailAddress(asea:AuthorizedSenderEmailAddress):Observable<any>{
        return this.umApiService.createAuthorizedSenderEmailAddress(asea).pipe(map((r)=>this.processCreateAuthorizedSenderEmailAddressResult(r)));

    }

    private processCreateAuthorizedSenderEmailAddressResult(r:AuthorizedSenderEmailAddressResponse){
        if(r.success && r.authorizedSenderEmailAddressList && r.authorizedSenderEmailAddressList.length>0){
            var s = this.processAuthorizedSenderEmailAddressDTO(r.authorizedSenderEmailAddressList[0]);
            if(!this.authorizedSenderEmailAddressCache[s.customerId]) this.authorizedSenderEmailAddressCache[s.customerId]=[];
            this.authorizedSenderEmailAddressCache[s.customerId]!.push(s);
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public updateAuthorizedSenderEmailAddress(asea:AuthorizedSenderEmailAddress){
        return this.umApiService.updateAuthorizedSenderEmailAddress(asea).pipe(map((r)=>this.processUpdateAuthorizedSenderEmailAddress(r)));
    }

    private processUpdateAuthorizedSenderEmailAddress(r:AuthorizedSenderEmailAddressResponse){
        if(r.success && r.authorizedSenderEmailAddressList && r.authorizedSenderEmailAddressList.length>0){
            var s = this.processAuthorizedSenderEmailAddressDTO(r.authorizedSenderEmailAddressList[0]);
            let index = this.authorizedSenderEmailAddressCache[s.customerId]!.findIndex(ea=>ea.id==s.id);
            if(index>-1){
                this.authorizedSenderEmailAddressCache[s.customerId]![index]=s;
            }
            else{
                this.authorizedSenderEmailAddressCache[s.customerId]!.push(s);
            }
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public enableAuthorizedSenderEmailAddress(asea:AuthorizedSenderEmailAddress, enable:boolean){
        return this.umApiService.enableAuthorizedSenderEmailAddress(asea.id, enable).pipe(map((r)=>this.processEnableAuthorizedSenderEmailAddressResult(asea,enable,r)));
    }

    private processEnableAuthorizedSenderEmailAddressResult(asea:AuthorizedSenderEmailAddress, enable:boolean, r:BasicResponse){
        if(r.success){
            asea.enabled=enable;
        }
        return r;
    }

    //#endregion

    //#region customer fee schedule
    public getCustomerFeeSchedule(customerId:number, billingCodeType:string){
        return this.umApiService.getCustomerFeeSchedule( customerId, billingCodeType).pipe(map((r)=>this.processCustomerFeeScheduleResult(r, customerId)), share());
    }

    private processCustomerFeeScheduleResult(r:CustomerFeeScheduleResponse, customerId:number):CustomerFeeSchedule|undefined{
        if(r.success && r.customerFeeSchedule){
            return CustomerFeeSchedule.mapFromDTO(r.customerFeeSchedule);
        }
        return undefined;
    }

    public updateCustomerFeeSchedule(cfs:CustomerFeeSchedule){
        return this.umApiService.updateCustomerFeeSchedule(cfs).pipe(map((r)=>this.processUpdateCustomerFeeSchedule(r)));
    }

    private processUpdateCustomerFeeSchedule(r:CustomerFeeScheduleResponse){
        return r;
    }
    //#endregion

    //#region customer branding
    public getCustomerBrandings(customerId:number, forceReload:boolean){
        if(forceReload) this.customerBrandingCache[customerId]=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if(this.customerBrandingCache[customerId]) return of(this.customerBrandingCache[customerId]);
        // Request pending
        else if(this.customerBrandingObservableCache[customerId]) return this.customerBrandingObservableCache[customerId];
        // New request needed
        else this.customerBrandingObservableCache[customerId] = this.fetchCustomerBrandings(customerId);
        return this.customerBrandingObservableCache[customerId];
    }

    public fetchCustomerBrandings(customerId:number){
        return this.umApiService.getCustomerBrandings(customerId).pipe(map((r)=>this.processCustomerBrandingsResult(r, customerId)), share());
    }

    private processCustomerBrandingsResult(r:CustomerBrandingResponse, customerId:number){
        this.customerBrandingObservableCache[customerId]=undefined;
        var customerBrandings = new Array<CustomerBranding>();
        if(r.success && r.customerBrandingList){
            customerBrandings = r.customerBrandingList.map(dto=>this.processCustomerBrandingDTO(dto));
        }
        this.customerBrandingCache[customerId]=customerBrandings;
        return customerBrandings;
    }

    private processCustomerBrandingDTO(dto:CustomerBrandingDTO):CustomerBranding{
        return CustomerBranding.mapFromDTO(dto);
    }

    public createCustomerBranding(b:CustomerBranding, file:File):Observable<any>{
        return this.umApiService.createBranding(b, file).pipe(map((r)=>this.processCreateCustomerBrandingResult(r)));
    }

    private processCreateCustomerBrandingResult(r:CustomerBrandingResponse){
        if(r.success && r.customerBrandingList && r.customerBrandingList.length>0) {
            var b = this.processCustomerBrandingDTO(r.customerBrandingList[0]);
            if(!this.customerBrandingCache[b.customerId])this.customerBrandingCache[b.customerId]=[];
            this.customerBrandingCache[b.customerId]!.push(b);
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public updateCustomerBranding(b: CustomerBranding, file: File){
        return this.umApiService.updateCustomerBranding(b, file).pipe(map((r)=>this.processUpdateCustomerBrandingResult(r,b.customerId)));
    }

    private processUpdateCustomerBrandingResult(r:CustomerBrandingResponse,customerId:number){
        if(r.success){
            this.customerBrandingCache[customerId]=undefined;
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public deleteCustomerBranding(b:CustomerBranding){
        return this.umApiService.deleteCustomerBranding(b).pipe(map((r)=>this.processDeleteCustomerBrandingResult(b, r)));
    }

    private processDeleteCustomerBrandingResult(b:CustomerBranding, r:BasicResponse){
        if(r.success){
            this.customerBrandingCache[b.customerId]=undefined;
        }
        return r;
    }
    //#endregion

    //#region customer security compliance
    public getCustomerSecurityCompliance(customerId:number, forceReload:boolean){
        if(forceReload) this.customerSecurityComplianceCache[customerId]=undefined;
        if(!this.authorized) return of(undefined);
        // Data available
        if(this.customerSecurityComplianceCache[customerId]) return of(this.customerSecurityComplianceCache[customerId]);
        // Request pending
        else if(this.customerSecurityComplianceObservableCache[customerId]) return this.customerSecurityComplianceObservableCache[customerId];
        // New request needed
        else this.customerSecurityComplianceObservableCache[customerId] = this.fetchCustomerSecurityCompliance(customerId);
        return this.customerSecurityComplianceObservableCache[customerId];
    }

    public fetchCustomerSecurityCompliance(customerId:number){
        return this.umApiService.getCustomerSecurityCompliance(customerId).pipe(map((r)=>this.processCustomerSecurityComplianceResult(r, customerId)), share());
    }

    private processCustomerSecurityComplianceResult(r:CustomerSecurityComplianceResponse, customerId:number){
        this.customerSecurityComplianceObservableCache[customerId]= of(undefined);
        if(r.success && r.customerSecurityCompliance){
            this.customerSecurityComplianceCache[customerId] = this.processCustomerSecurityComplianceDTO(r.customerSecurityCompliance);
        }
        return this.customerSecurityComplianceCache[customerId];
    }

    private processCustomerSecurityComplianceDTO(dto:CustomerSecurityComplianceDTO):CustomerSecurityCompliance{
        return CustomerSecurityCompliance.mapFromDTO(dto);
    }

    public updateCustomerSecurityCompliance(csc: CustomerSecurityCompliance){
        return this.umApiService.updateCustomerSecurityCompliance(csc).pipe(map((r)=>this.processUpdateCustomerSecurityComplianceResult(r)));
    }

    private processUpdateCustomerSecurityComplianceResult(r:CustomerSecurityComplianceResponse){
        if(r.success && r.customerSecurityCompliance) {
            var csc = this.processCustomerSecurityComplianceDTO(r.customerSecurityCompliance);
            this.customerSecurityComplianceCache[csc.customerId]=csc;
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }
    //#endregion

    //#region premier partners and services
    public getPremierServiceTypes(forceReload:boolean){
        if(forceReload) this.premierServiceTypeCache=undefined;
        if(!this.authorized) return of([]);
        //Data available
        if(this.premierServiceTypeCache) return of(this.premierServiceTypeCache);
        //Request pending
        else if(this.premierServiceTypeObservableCache) return this.premierServiceTypeObservableCache;
        //New request needed
        else this.premierServiceTypeObservableCache=this.fetchPremierServiceTypes();
        return this.premierServiceTypeObservableCache;
    }

    public fetchPremierServiceTypes(){
        return this.umApiService.getPremierServiceTypes().pipe(map((r)=>this.processPremierServiceTypesResult(r)), share());
    }

    private processPremierServiceTypesResult(r:PremierServiceTypeReponse):PremierServiceType[]{
        this.premierServiceTypeObservableCache=undefined;
        var serviceTypes = new Array<PremierServiceType>();
        if(r.success && r.premierServiceTypeList){
            serviceTypes = r.premierServiceTypeList.map(dto=>PremierServiceType.mapFromDTO(dto));
        }
        this.premierServiceTypeCache=serviceTypes;
        return serviceTypes;
    }

    public getPremierServiceProducts(forceReload:boolean){
        if(forceReload) this.premierServiceProductCache=undefined;
        if(!this.authorized) return of([]);
        //Data available
        if(this.premierServiceProductCache) return of(this.premierServiceProductCache);
        //Request pending
        else if(this.premierServiceProductObservableCache) return this.premierServiceProductObservableCache;
        //New request needed
        else this.premierServiceProductObservableCache=this.fetchPremierServiceProducts();
        return this.premierServiceProductObservableCache;
    }

    public fetchPremierServiceProducts(){
        return this.umApiService.getPremierServiceProducts().pipe(map((r)=>this.processPremierServiceProductsResult(r)), share());
    }

    private processPremierServiceProductsResult(r:PremierServiceProductsReponse):PremierServiceProduct[]{
        this.premierServiceTypeObservableCache=undefined;
        var serviceProducts = new Array<PremierServiceProduct>();
        if(r.success && r.premierServiceProductList){
            serviceProducts = r.premierServiceProductList.map(dto=>PremierServiceProduct.mapFromDTO(dto));
        }
        this.premierServiceProductCache=serviceProducts;
        return serviceProducts;
    }
    
    public createPremierServiceProduct(psp:PremierServiceProduct):Observable<any>{
        return this.umApiService.createPremierServiceProduct(psp).pipe(map((r)=>this.processCreatePremierServiceProductResult(psp, r)));
    }

    private processCreatePremierServiceProductResult(psp:PremierServiceProduct, r:BasicResponse){
        if(r.success){
        //TODO: not yet implemented in UI
        }
        return r;
    }

    public updatePremierServiceProduct(psp:PremierServiceProduct):Observable<any>{
        return this.umApiService.updatePremierServiceProduct(psp).pipe(map((r)=>this.processUpdatePremierServiceProductResult(psp, r)));
    }

    private processUpdatePremierServiceProductResult(psp:PremierServiceProduct, r:BasicResponse){
        if(r.success){
        //TODO: not yet implemented in UI
        }
        return r;
    }

    public enablePremierServiceProduct(psp:PremierServiceProduct, enable:boolean){
        return this.umApiService.enablePremierServiceProduct(psp.id, enable).pipe(map((r)=>this.processEnablePremierServiceProductResult(psp,enable,r)));
    }

    private processEnablePremierServiceProductResult(psp:PremierServiceProduct, enabled:boolean, r:BasicResponse){
        if(r.success){
            psp.enabled=enabled;
        }
        return r;
    }

    public getPremierServiceProductTypes(forceReload:boolean){
        if(forceReload) this.premierServiceProductTypeCache=undefined;
        if(!this.authorized) return of([]);
        //Data available
        if(this.premierServiceProductTypeCache) return of(this.premierServiceProductTypeCache);
        //Request pending
        else if(this.premierServiceProductTypeObservableCache) return this.premierServiceProductTypeObservableCache; 
        //New request needed
        else this.premierServiceProductTypeObservableCache=this.fetchPremierServiceProductTypes();
        return this.premierServiceProductTypeObservableCache;
    }

    public fetchPremierServiceProductTypes(){
        return this.umApiService.getPremierServiceProductTypes().pipe(map((r)=>this.processPremierServiceProductTypesResult(r)), share());
    }

    private processPremierServiceProductTypesResult(r:PremierServiceProductTypeResponse):PremierServiceProductType[]{
        this.premierServiceProductTypeCache=undefined;
        var productTypes = new Array<PremierServiceProductType>();
        if(r.success){
            productTypes = r.premierServiceProductTypeList.map(dto=>PremierServiceProductType.mapFromDTO(dto));
        }
        this.premierServiceProductTypeCache=productTypes;
        return productTypes;
    }

    public getPremierPartners(forceReload:boolean){
        if(forceReload) this.premierPartnerCache=undefined;
        if(!this.authorized) return of([]);
        //Data available
        if(this.premierPartnerCache) return of(this.premierPartnerCache);
        //Request pending
        else if(this.premierPartnersObservableCache) return this.premierPartnersObservableCache;
        //New request needed
        else this.premierPartnersObservableCache= this.fetchPremierPartners();
        return this.premierPartnersObservableCache;
    }

    public fetchPremierPartners(){
        let requestorCustomerId = this.authService.customerContext?.id;
        return this.umApiService.getPremierPartners(requestorCustomerId).pipe(map((r)=>this.processPremierPartnersResult(r)), share());
    }

    private processPremierPartnersResult(r:PremierPartnerResponse):PremierPartner[]{
        this.premierPartnersObservableCache=undefined;
        var premierPartners = new Array<PremierPartner>();
        if(r.success){  
           premierPartners = r.premierPartnerList.map(dto=>PremierPartner.mapFromDTO(dto));
        }
        this.premierPartnerCache=premierPartners;
        return this.premierPartnerCache;
    }

    public  getPremierPartnerById(partnerId:number){
        return this.getPremierPartners(false).pipe(map(partnerArray=>partnerArray?.find(p=>p.id==partnerId)), share());
    }

    public  getPremierPartnerByProviderName(providerName:string){
        return this.getPremierPartners(false).pipe(map(partnerArray=>partnerArray?.find(p=>p.providerName==providerName)), share());
    }

    public getCustomerPremierPartnerServiceConfigurations(forceReload:boolean){
        if(forceReload) this.customerPremierPartnerServiceConfigurationCache=undefined;
        if(!this.authorized) return of([]);
        //Data available
        if(this.customerPremierPartnerServiceConfigurationCache) return of(this.customerPremierPartnerServiceConfigurationCache);
        //Request pending
        else if(this.customerPremierPartnerServiceConfigurationObservableCache) return this.customerPremierPartnerServiceConfigurationObservableCache;
        //New request
        else this.customerPremierPartnerServiceConfigurationObservableCache=this.fetchCustomerPremierPartnerServiceConfigurations();
        return this.customerPremierPartnerServiceConfigurationObservableCache;
    }

    public fetchCustomerPremierPartnerServiceConfigurations(){
        let customerId=this.authService.customerContext!.id;
        return this.umApiService.getCustomerPremierPartnerServiceConfigurations(customerId).pipe(map((r)=>this.processCustomerPremierPartnerServiceConfigurationsResult(r)), share());
    }

    private processCustomerPremierPartnerServiceConfigurationsResult(r:CustomerPremierPartnerServiceConfigurationResponse):CustomerPremierPartnerServiceConfiguration[]{
        this.customerPremierPartnerServiceConfigurationObservableCache=undefined;
        var customerPremierPartnerServiceConfigurations = new Array<CustomerPremierPartnerServiceConfiguration>();
        if(r.success && r.serviceConfigurationList){
            
            customerPremierPartnerServiceConfigurations = r.serviceConfigurationList.map(dto=>CustomerPremierPartnerServiceConfiguration.mapFromDTO(dto));
        }
        this.customerPremierPartnerServiceConfigurationCache=customerPremierPartnerServiceConfigurations;
        return this.customerPremierPartnerServiceConfigurationCache;
    }

    public createCustomerPremierPartnerServiceConfiguration(cpsc:CustomerPremierPartnerServiceConfiguration):Observable<any>{
        return this.umApiService.createCustomerPremierPartnerServiceConfiguration(cpsc).pipe(map((r)=>this.processCreateCustomerPremierPartnerServiceConfigurationResult(cpsc, r)));

    }

    private processCreateCustomerPremierPartnerServiceConfigurationResult(cpsc:CustomerPremierPartnerServiceConfiguration, r:BasicResponse){
        if(r.success){
            this.customerPremierPartnerServiceConfigurationCache=undefined;
        }
        return r;
    }

    public updateCustomerPremierPartnerServiceConfiguration(cpsc:CustomerPremierPartnerServiceConfiguration){
        return this.umApiService.updateCustomerPremierPartnerServiceConfiguration(cpsc).pipe(map((r)=>this.processUpdateCustomerPremierPartnerServiceConfigurationResult(cpsc, r)));
    }

    private processUpdateCustomerPremierPartnerServiceConfigurationResult(cpsc:CustomerPremierPartnerServiceConfiguration, r:BasicResponse){
        if(r.success){
            this.customerPremierPartnerServiceConfigurationCache=undefined;
        }
        return r;
    }

    public deleteCustomerPremierPartnerServiceConfiguration(cpsc:CustomerPremierPartnerServiceConfiguration){
        return this.umApiService.deleteCustomerPremierPartnerServiceConfiguration(cpsc).pipe(map((r)=>this.processDeleteCustomerPremierPartnerServiceConfigurationResult(cpsc, r)));
    }

    private processDeleteCustomerPremierPartnerServiceConfigurationResult(cpsc:CustomerPremierPartnerServiceConfiguration, r:BasicResponse){
        if(r.success){
            this.customerPremierPartnerServiceConfigurationCache=undefined;
        }
        return r;
    }
    //#endregion

    //#region qb
    public getIsQBAuthorized(customerId:number,clientIds:number[]){
        return this.umApiService.qbIsEntityAuthorized(customerId, clientIds).pipe(map((r)=>this.processIsQBAuthorized(r)));
    }

    public getIsQBAuthorizedExternal(token:string){
        return this.umApiService.qbIsEntityAuthorizedExternal(token).pipe(map((r)=>this.processIsQBAuthorized(r)));
    }

    private processIsQBAuthorized(r:QBIsEntityAuthorizedResponse){
        if(r.success){
        }
        return r;
    }

    public deleteQBEntity(customerId:number, clientId:number){
        return this.umApiService.qbDeleteEntity(customerId, clientId).pipe(map((r)=>this.processDeleteQBEntity(r)));
    }

    private processDeleteQBEntity(r:BasicResponse){
        if(r.success){
        }
        return r;
    }

    public getQBAllAccounts(customerId:number,clientId:number){
        return this.umApiService.qbAllAccounts(customerId,clientId).pipe(map((r)=>this.processQBAllAccounts(r)));
    }

    private processQBAllAccounts(r:QBAllAccountsResponse){
        if(r.success){
        }
        return r;
    }

    public getQBAllClasses(customerId:number,clientId:number){
        return this.umApiService.qbAllClasses(customerId,clientId).pipe(map((r)=>this.processQBAllClasses(r)));
    }

    private processQBAllClasses(r:QBAllClassesResponse){
        if(r.success){
        }
        return r;
    }

    public getQBAllDepartments(customerId:number,clientId:number){
        return this.umApiService.qbAllDepartments(customerId,clientId).pipe(map((r)=>this.processQBAllDepartments(r)));
    }

    private processQBAllDepartments(r:QBAllDepartmentsResponse){
        if(r.success){
        }
        return r;
    }
    //#endregion

    //#region xero
    public getIsXeroAuthorized(customerId:number,clientIds:number[]){
        return this.umApiService.xeroIsEntityAuthorized(customerId, clientIds).pipe(map((r)=>this.processIsXeroAuthorized(r)));
    }

    public getIsXeroAuthorizedExternal(token:string){
        return this.umApiService.xeroIsEntityAuthorizedExternal(token).pipe(map((r)=>this.processIsXeroAuthorized(r)));
    }

    private processIsXeroAuthorized(r:XeroIsEntityAuthorizedResponse){
        if(r.success){
        }
        return r;
    }

    public deleteXeroEntity(customerId:number, clientId:number){
        return this.umApiService.xeroDeleteEntity(customerId, clientId).pipe(map((r)=>this.processDeleteXeroEntity(r)));
    }

    private processDeleteXeroEntity(r:BasicResponse){
        if(r.success){
        }
        return r;
    }

    public getXeroAllAccounts(customerId:number,clientId:number){
        return this.umApiService.xeroAllAccounts(customerId,clientId).pipe(map((r)=>this.processXeroAllAccounts(r)));
    }

    private processXeroAllAccounts(r:XeroAllAccountsResponse){
        if(r.success){
        }
        return r;
    }

    public getXeroAllTrackingCategories(customerId:number,clientId:number){
        return this.umApiService.xeroAllTrackingCategories(customerId,clientId).pipe(map((r)=>this.processXeroAllTrackingCategories(r)));
    }

    private processXeroAllTrackingCategories(r:XeroAllTrackingCategoriesResponse){
        if(r.success){
        }
        return r;
    }
    //#endregion

    //#region microsoft
    public getIsMicrosoftAuthorized(customerId:number,clientIds:number[]){
        return this.umApiService.microsoftIsEntityAuthorized(customerId, clientIds).pipe(map((r)=>this.processIsMicrosoftAuthorized(r)));
    }

    public getIsMicrosoftAuthorizedExternal(token:string){
        return this.umApiService.microsoftIsEntityAuthorizedExternal(token).pipe(map((r)=>this.processIsMicrosoftAuthorized(r)));
    }

    private processIsMicrosoftAuthorized(r:MicrosoftIsEntityAuthorizedResponse){
        if(r.success){
        }
        return r;
    }

    public disconnectMicrosoftEntity(customerId:number, clientId:number){
        return this.umApiService.microsoftDisconnectEntity(customerId, clientId);
    }

    public deleteMicrosoftEntity(customerId:number, clientId:number){
        return this.umApiService.microsoftDeleteEntity(customerId, clientId).pipe(map((r)=>this.processDeleteMicrosoftEntity(r)));
    }

    private processDeleteMicrosoftEntity(r:BasicResponse){
        if(r.success){
        }
        return r;
    }
    //#endregion

    //#region dropbox
    public getIsDropboxAuthorized(customerId:number,clientIds:number[]){
        let requestorId = this.authService.currentUserValue?this.authService.currentUserValue!.id:0;
        return this.umApiService.dropboxIsEntityAuthorized(requestorId, customerId, clientIds).pipe(map((r)=>this.processIsDropboxAuthorized(r)));
    }

    public getIsDropboxAuthorizedExternal(token:string){
        return this.umApiService.dropboxIsEntityAuthorizedExternal(token).pipe(map((r)=>this.processIsDropboxAuthorized(r)));
    }

    private processIsDropboxAuthorized(r:DropboxIsEntityAuthorizedResponse){
        if(r.success){
        }
        return r;
    }

    public dropboxRevokeToken(customerId:number,clientId:number){
        let requestorId = this.authService.currentUserValue?this.authService.currentUserValue!.id:0;
        return this.umApiService.dropboxRevokeToken(requestorId, customerId, clientId).pipe(map((r)=>this.processDropboxRevokeToken(r)));
    }

    private processDropboxRevokeToken(r:BasicResponse){
        if(r.success){
        }
        return r;
    }
    //#endregion

    //#region prismHR
    public prismHRAPIPermissions(api_url:string, username:string, password:string, peoId:string){
        return this.umApiService.prismHRAPIPermissions(api_url, username, password, peoId).pipe(map((r)=>this.processPrismHRAPIPermissions(r)));
    }
    private processPrismHRAPIPermissions(r:PrismHRAPIPermissionsResponse){
        if(r.success){
            let response=new PrismHRAPIPermissionsResponse();
            Object.assign(response,r);
            return response;
        }
        return r;
    }
    //#endregion

    //#region file sharing
    public getFileSharingAccounts(customerId:number, forceReload:boolean){
        if(forceReload) this.fileSharingAccountCache[customerId]=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if(this.fileSharingAccountCache[customerId]) return of(this.fileSharingAccountCache[customerId]);
        // Request pending
        else if(this.fileSharingAccountObservableCache[customerId]) return this.fileSharingAccountObservableCache[customerId];
        // New request needed
        else this.fileSharingAccountObservableCache[customerId] = this.fetchFileSharingAccounts(customerId);

        return this.fileSharingAccountObservableCache[customerId];

    }

    public fetchFileSharingAccounts(customerId:number){
        return this.umApiService.getFileSharingAccounts(customerId).pipe(map((r)=>this.processFileSharingAccountsResult(r, customerId)), share());
    }

    private processFileSharingAccountsResult(r:CustomerFileSharingAccountResponse, customerId:number):FileSharingAccount[]{
        this.fileSharingAccountObservableCache[customerId]=undefined;
        var fileSharingAccounts = new Array<FileSharingAccount>();
        if(r.success && r.fileSharingAccountList){
            fileSharingAccounts = r.fileSharingAccountList.map(dto=>this.processFileSharingAccountDTO(dto));
        }
        this.fileSharingAccountCache[customerId]=fileSharingAccounts;
        return fileSharingAccounts;
    }

    private processFileSharingAccountDTO(dto:FileSharingAccountDTO):FileSharingAccount{
        var fileSharingAccount = FileSharingAccount.mapFromDTO(dto);
        if(this.authService.isSuperAdmin){
            this.getCustomerById(fileSharingAccount.customerId).pipe(take(1)).subscribe(c=> fileSharingAccount.customerId = c!.id);
        }
        else{
            fileSharingAccount.customerId = this.authService.customerContext!.id;
        }
        return fileSharingAccount;
    }

    public updateFileSharingAccount(fsa:FileSharingAccount){
        return this.umApiService.updateFileSharingAccount(fsa).pipe(map((r)=>this.processUpdateFileSharingAccount(r,fsa.customerId)));
    }

    private processUpdateFileSharingAccount(r:CustomerFileSharingAccountResponse,customerId:number){
        if(r.success){
            this.fileSharingAccountCache[customerId]=undefined;
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public deleteFileSharingAccount(fsa:FileSharingAccount){
        return this.umApiService.deleteFileSharingAccount(fsa).pipe(map((r)=>this.processDeleteFileSharingAccount(r,fsa.customerId)));
    }

    private processDeleteFileSharingAccount(r:BasicResponse,customerId:number){
        if(r.success){
            this.fileSharingAccountCache[customerId]=undefined;
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }
    //#endregion

    //#region billing buckets
    public getBillingBuckets(forceReload:boolean){
        if(forceReload) this.billingBucketCache=undefined;
        if(!this.authorized) return of([]);
        // Data available
        if (this.billingBucketCache) return of(this.billingBucketCache);
        // Request pending
        else if (this.billingBucketObservableCache) return this.billingBucketObservableCache;
        // New request needed
        else this.billingBucketObservableCache = this.fetchBillingBuckets();

        return this.billingBucketObservableCache;
    }

    public fetchBillingBuckets(){
        return this.umApiService.getBillingBuckets().pipe(map((r)=>this.processBillingBucketsResult(r)), share());
    }

    private processBillingBucketsResult(r:BillingBucketResponse):BillingBucket[]{
        this.billingBucketObservableCache=undefined;
        var billingBuckets = new Array<BillingBucket>();
        if(r.success && r.billingBucketList) {
            billingBuckets = r.billingBucketList.map(dto => this.processBillingBucketDTO(dto));
        }
        this.billingBucketCache=billingBuckets;
        return billingBuckets;
    }

    private processBillingBucketDTO(dto:BillingBucketDTO):BillingBucket{
        var billingBucket = BillingBucket.mapFromDTO(dto);
        if(this.authService.isSuperAdmin){
            this.getVendorById(billingBucket.vendorId!).pipe(take(1)).subscribe(v => billingBucket.vendor = v);
        }
        else{
            billingBucket.vendor = this.authService.vendorContext;
        }
        return billingBucket;
    }

    public createBillingBucket(b:BillingBucket):Observable<any>{
        return this.umApiService.createBillingBucket(b).pipe(map((r)=>this.processCreateBillingBucketResult(r)));
    }

    private processCreateBillingBucketResult(r:BillingBucketResponse){
        if(r.success && r.billingBucketList && r.billingBucketList.length>0) {
            var b = this.processBillingBucketDTO(r.billingBucketList[0]);
            this.billingBucketCache!.push(b);
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public updateBillingBucket(b:BillingBucket){
        return this.umApiService.updateBillingBucket(b).pipe(map((r)=>this.processUpdateBillingBucketResult(r)));
    }

    private processUpdateBillingBucketResult(r:BillingBucketResponse){
        if(r.success && r.billingBucketList && r.billingBucketList.length>0) {
            var b = this.processBillingBucketDTO(r.billingBucketList[0]);
            let index = this.billingBucketCache!.findIndex(bb=>bb.id==b.id);
            if(index>-1){
                this.billingBucketCache![index]=b;
            }
            else{
                this.billingBucketCache!.push(b);
            }
        }
        else{
            this.handleError(typeof(r), r.message);
        }
        return r;
    }

    public enableBillingBucket(b:BillingBucket, enable:boolean){
        return this.umApiService.enableBillingBucket(b.id, enable).pipe(map((r)=>this.processEnableBillingBucketResult(b, enable, r)));
    }

    private processEnableBillingBucketResult(b:BillingBucket, enable:boolean, r:BasicResponse){
        if(r.success) {
           b.enabled=enable;
        }
        return r;
    }
    //#endregion

    //#region wizardSession
    public setWizardSession(ws:WizardSession):Observable<WizardSession | undefined>{
        return this.umApiService.setWizardSession(ws).pipe(map((r:WizardSessionResponse) => {
            return r.success ? WizardSession.mapFromDTO(r.wizardSession) : undefined;
        }));
    }

    public getWizardSession():Observable<WizardSession | undefined>{
        return this.umApiService.getWizardSession().pipe(map((r:WizardSessionResponse) => {
            return r.success ? WizardSession.mapFromDTO(r.wizardSession) : undefined;
        }));
    }

    //#endregion

    private handleError(source: string, message: string, navigateToError: boolean = false) {
        const stack = new Error().stack;
        const timestamp = new Date().toISOString();
        console.error(`[${timestamp}]`,`[handleError] source=${source} message=${message}`, stack);
        if (navigateToError) {
            this.router.navigate(['/error']);
        }
    }
}